<template>
  <div>
    <div
      v-if="orderType === 'Delivery' || orderType === 'Takeaway'"
      class="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
    >
      <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
        <svg
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        Información del Cliente
      </h5>

      <div class="space-y-2 text-sm">
        <div
          v-if="selectedCustomer?.name || customerName"
          class="flex items-center gap-2"
        >
          <span class="text-gray-600 dark:text-gray-400 font-medium">Cliente:</span>
          <span class="text-gray-900 dark:text-gray-100">{{ selectedCustomer?.name || customerName }}</span>
        </div>

        <div
          v-if="selectedCustomer?.phone"
          class="flex items-center gap-2"
        >
          <svg
            class="h-3 w-3 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <span class="text-gray-600 dark:text-gray-400">Teléfono:</span>
          <span class="text-gray-900 dark:text-gray-100">{{ selectedCustomer.phone }}</span>
        </div>

        <div
          v-if="orderType === 'Delivery' && (deliveryAddress || selectedAddress)"
          class="flex items-start gap-2"
        >
          <svg
            class="h-3 w-3 mt-0.5 text-gray-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <div class="flex-1">
            <span class="text-gray-600 dark:text-gray-400">Dirección de entrega:</span>
            <div class="text-gray-900 dark:text-gray-100 mt-1">
              <span
                v-if="deliveryAddress"
                class="whitespace-pre-line"
              >{{ deliveryAddress }}</span>
              <span
                v-else-if="selectedAddress"
                class="whitespace-pre-line"
              >
                {{ formattedSelectedAddress }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-else-if="orderType === 'Takeaway' && selectedCustomer && (selectedCustomer.address || selectedCustomer.neighborhood)"
          class="flex items-start gap-2"
        >
          <svg
            class="h-3 w-3 mt-0.5 text-gray-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <div class="flex-1">
            <span class="text-gray-600 dark:text-gray-400">Dirección del cliente:</span>
            <div class="text-gray-900 dark:text-gray-100 mt-1">
              {{ formattedCustomerAddress }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="displayOrderNotes"
      class="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
    >
      <h5 class="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-2">
        <svg
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
        Notas de la Orden
      </h5>
      <p class="text-sm text-amber-900 dark:text-amber-100 whitespace-pre-line">
        {{ displayOrderNotes }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { OrderAddress, OrderCustomer } from './orderSummaryTypes';

interface Props {
  orderType?: string;
  selectedCustomer?: OrderCustomer | null;
  selectedAddress?: OrderAddress | null;
  customerName?: string;
  deliveryAddress?: string;
  orderNotes?: string;
}

const props = defineProps<Props>();

const formatAddress = (address?: OrderAddress | OrderCustomer | null): string => {
  if (!address) {
    return '';
  }

  const parts: string[] = [];
  if (address.address) parts.push(address.address);
  if (address.apartment) parts.push(`#${address.apartment}`);
  if (address.neighborhood) parts.push(`Col. ${address.neighborhood}`);
  if (address.city) parts.push(address.city);
  if (address.postal_code) parts.push(`CP ${address.postal_code}`);
  if (address.reference_point) parts.push(`Ref: ${address.reference_point}`);

  return parts.join(', ');
};

const displayOrderNotes = computed(() => props.orderNotes || '');
const formattedSelectedAddress = computed(() => formatAddress(props.selectedAddress));
const formattedCustomerAddress = computed(() => formatAddress(props.selectedCustomer));
</script>
